import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { red } from '../../Constants/Colors';
import { Button, Typography } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { ChevronLeft } from '@material-ui/icons';
import clsx from 'clsx';
import {
  isStartScreen,
  getCurrentStep,
  Steps,
  getSystems,
  getProductTypes,
  isCurrentStepOrLater,
  getSelectedProductType,
  isPreviousStepReady,
  isStepReady,
} from '../../Reducers/App/Selectors';
import { resetConfiguration } from '../../Reducers/Configurator/Actions';
import { useDispatch, useSelector } from 'react-redux';
import SystemSelector from '../Navigation/SystemSelector';
import Subtitle from './Subtitle';
import ProductTypeSelector from '../Navigation/ProductTypeSelector';
import { setCurrentStep, setProductParamsValid } from '../../Reducers/App/Actions';
import SaveAndLoad from './SaveAndLoad';
import ConfiguratorMenu from '../Navigation/ConfiguratorMenu';
import { logo } from '../../Resources/images/logo';
import Logout from './Logout';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
  },
  logoWrapper: {
    padding: '1.5rem',
    fontSize: '0.8em',
  },
  logo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.8rem',
  },
  upper: {
    textTransform: 'uppercase',
  },
  logoText: {
    color: red,
    fontWeight: 'bolder',
  },
  logoImage: {
    marginTop: '0.5rem',
    height: '65px',
    width: 'auto',
  },
  subTitle: {
    marginTop: '1.5rem',
  },
  topNav: {
    minWidth: 'calc(100% - 462px)',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '3.4rem',
    position: 'absolute',
    right: 0,
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      right: 0,
      width: 'calc(100vw - 462px)',
      height: '5px',
      backgroundColor: red,
    },
  },
  selectors: {
    paddingLeft: '0.8em',
  },
  systemSelect: {
    PaddingBottom: '1.5rem',
  },
  navButton: {
    fontSize: '1em',
    border: 'none',
    minWidth: 'auto',
    textTransform: 'none',
    backgroundColor: 'transparent',
    '& .MuiButton-label': {
      padding: 0,
      fontWeight: 'normal',
    },
    '& .MuiButton-startIcon': {
      marginRight: 0,
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      color: 'white',
      backgroundColor: 'transparent',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  navButtonSeparator: {
    color: 'white',
    fontSize: '1.2rem',
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  navButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '4px',
  },
}));

export const isStepAccessible =
  (step, previousStep = false) =>
  (state) =>
    isCurrentStepOrLater(step)(state) ||
    (previousStep && isPreviousStepReady(step)(state)) ||
    isStepReady(step)(state);

const Header = () => {
  const classes = useStyles();
  const startScreen = useSelector(isStartScreen);
  const currentStep = useSelector(getCurrentStep);
  const showProductSelect = currentStep === Steps.PRODUCT_TYPE_SELECT;
  const showSystemSelect = currentStep === Steps.SYSTEM_SELECT || showProductSelect;

  const showChangeProductParameters = useSelector(isStepAccessible(Steps.PRODUCT_PARAMETERS));
  const showChangeSystemParameters = useSelector(isStepAccessible(Steps.SYSTEM_PARAMETERS, true));
  const showComponents = useSelector(isStepAccessible(Steps.CONFIGURATOR, true));
  const showConfigurator = currentStep === Steps.CONFIGURATOR;

  const systems = useSelector(getSystems);
  const productTypes = useSelector(getProductTypes);
  const selectedProductType = useSelector(getSelectedProductType);

  const dispatch = useDispatch();
  const goToStep = (step) => dispatch(setCurrentStep(step));
  const resetProductParameterValidation = () => {
    dispatch(resetConfiguration());
    dispatch(setProductParamsValid(false));
  };

  const selectedProductTypeName = selectedProductType?.title;

  const SeparatorIcon = () => <span className={classes.navButtonSeparator}>/</span>;

  return (
    <>
      <div className={classes.root}>
        <div className={classes.logoWrapper}>
          <div className={classes.logo}>
            <Typography variant={'h4'} className={clsx(classes.logoText, classes.upper)}>
              Configurator
            </Typography>
            <img src={logo} className={classes.logoImage} />
          </div>
          {startScreen && <Subtitle className={classes.subTitle}>{t('titleBox.subtitle')}</Subtitle>}
        </div>
        <div className={classes.topNav}>
          <div className={classes.navButtons}>
            {selectedProductType && showChangeProductParameters && (
              <>
                <Button
                  className={classes.navButton}
                  startIcon={<ChevronLeft />}
                  onClick={() => {
                    resetProductParameterValidation();
                    goToStep(Steps.SYSTEM_SELECT);
                  }}
                >
                  {selectedProductTypeName || t('headerMenu.changeSystem')}
                </Button>
                <Button
                  className={classes.navButton}
                  startIcon={<SeparatorIcon />}
                  disabled={!showChangeProductParameters}
                  onClick={() => {
                    if (currentStep !== Steps.PRODUCT_PARAMETERS) {
                      goToStep(Steps.PRODUCT_PARAMETERS);
                    }
                  }}
                >
                  {t('headerMenu.productParameters')}
                </Button>
                <Button
                  className={classes.navButton}
                  startIcon={<SeparatorIcon />}
                  disabled={!showChangeSystemParameters}
                  onClick={() => {
                    if (currentStep !== Steps.SYSTEM_PARAMETERS) {
                      goToStep(Steps.SYSTEM_PARAMETERS);
                    }
                  }}
                >
                  {t('headerMenu.systemParameters')}
                </Button>
                <Button
                  className={classes.navButton}
                  startIcon={<SeparatorIcon />}
                  disabled={!showComponents}
                  onClick={() => {
                    if (currentStep !== Steps.CONFIGURATOR) {
                      goToStep(Steps.CONFIGURATOR);
                    }
                  }}
                >
                  {t('headerMenu.components')}
                </Button>
              </>
            )}
          </div>

          <div>
            <SaveAndLoad />
            <Logout />
          </div>
        </div>
      </div>
      <div className={classes.selectors}>
        <div className={classes.systemSelect}>
          {showSystemSelect && <SystemSelector items={systems} title={'system.select'} />}
        </div>
        {showProductSelect && <ProductTypeSelector items={productTypes} title={'productType.select'} />}
        {showConfigurator && <ConfiguratorMenu />}
      </div>
    </>
  );
};

export default Header;
